// Modal «Elige el modelo»: la matriz color × capacidad de un modelo. Tocar una celda elige ese SKU.
import { Button, Modal } from '@/kit';
import {
    variantCountLabel,
    variantMatrix,
    type ModelGroup,
} from '@/domains/sales/portal/catalog/lib/modelGroups';
import { formatMXN } from '@/shared/lib/format/money';
import { VariantMatrix, type MatrixGridRow } from '@/shared/patterns/VariantPicker';
import { stockCount } from '../catalogAvailability';
import type { CatalogItem } from '../types';

const piecesLabel = (count: number): string =>
    `${String(count)} ${count === 1 ? 'pieza' : 'piezas'}`;

/** Filas de la matriz; sin piezas no se elige, salvo `reservedSku` (ya apartado en la sesión). */
export function matrixRowsOf(
    group: ModelGroup,
    selectedSku: string | null,
    reservedSku?: string | null,
): { columns: { key: string; label: string }[]; rows: MatrixGridRow[] } {
    const { columns, rows } = variantMatrix(group);
    return {
        columns,
        rows: rows.map((row) => ({
            key: row.key,
            label: row.color === '' ? 'Sin color' : row.color,
            cells: row.cells.map((options) =>
                options.map((option) => ({
                    key: option.item.sku,
                    price: formatMXN(option.item.cashPriceCents / 100),
                    pieces: piecesLabel(stockCount(option.item)),
                    tag: option.tag,
                    selected: option.item.sku === selectedSku,
                    disabled: !option.inStock && option.item.sku !== reservedSku,
                })),
            ),
        })),
    };
}

export function VariantMatrixDialog({
    group,
    open,
    selectedSku,
    reservedSku,
    onClose,
    onPick,
}: {
    group: ModelGroup;
    open: boolean;
    /** La variante elegida en la tarjeta; `null` si aún no hay. */
    selectedSku: string | null;
    /** SKU de la línea de la sesión: se elige aunque ya no tenga piezas. */
    reservedSku?: string | null;
    onClose: () => void;
    onPick: (item: CatalogItem) => void;
}) {
    const { columns, rows } = matrixRowsOf(group, selectedSku, reservedSku);
    return (
        <Modal
            isOpen={open}
            onClose={onClose}
            size="lg"
            title={`${group.brand} · ${group.model}`}
            description={`${variantCountLabel(group)} · toca la que necesitas`}
        >
            <VariantMatrix
                columns={columns}
                rows={rows}
                onSelect={(sku) => {
                    const item = group.variants.find((variant) => variant.sku === sku);
                    if (item) onPick(item);
                }}
            />
            <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={onClose}>
                    Cerrar
                </Button>
            </div>
        </Modal>
    );
}
